import type { CSSProperties } from 'react';

/**
 * EDGE FADE — the one way content fades out at the edge of a scroller, a
 * truncated label or a clipped row: a MASK on the content itself, so it
 * dissolves into whatever surface sits behind it, in both themes and on every
 * elevation step.
 *
 * Never paint the fade as a gradient overlay in a fixed colour
 * (`bg-gradient-to-r from-secondary-50 …`): that colour only matches one
 * surface, so on any other — a lifted track, a glass footer, a dark-mode
 * panel — it shows as a dark band (design review 2026-10: "the fade outs are
 * out of DS").
 *
 * Web only: React Native has no `mask-image`; a native host either skips the
 * fade or uses `MaskedView`.
 */
export interface EdgeFadeEdges {
  top?: boolean;
  bottom?: boolean;
  left?: boolean;
  right?: boolean;
}

/** Default fade length in px. */
export const EDGE_FADE_PX = 24;

function axis(
  start: boolean | undefined,
  end: boolean | undefined,
  px: number,
  dir: string,
) {
  if (!start && !end) return null;
  const from = start ? `transparent, black ${px}px` : 'black';
  const to = end ? `black calc(100% - ${px}px), transparent` : 'black';
  return `linear-gradient(${dir}, ${from}, ${to})`;
}

/**
 * The mask style for the edges that should fade — spread it onto the element
 * whose CONTENT scrolls or overflows (not onto an overlay). Returns `undefined`
 * when no edge fades, so a scroller at rest carries no mask at all.
 */
export function edgeFadeMask(
  edges: EdgeFadeEdges,
  px = EDGE_FADE_PX,
): CSSProperties | undefined {
  const layers = [
    axis(edges.top, edges.bottom, px, 'to bottom'),
    axis(edges.left, edges.right, px, 'to right'),
  ].filter((layer): layer is string => layer !== null);
  if (layers.length === 0) return undefined;
  const mask = layers.join(', ');
  // Two axes intersect: content shows only where BOTH layers are opaque.
  const composite =
    layers.length > 1
      ? { maskComposite: 'intersect', WebkitMaskComposite: 'source-in' }
      : {};
  return { maskImage: mask, WebkitMaskImage: mask, ...composite } as CSSProperties;
}
